import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, ArrowLeft, Building2, Home, Wrench, FileText, DollarSign, Star, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/site/Logo";
import { NotificationBell } from "../components/site/NotificationBell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const fmt = (s) => { try { return new Date(s).toLocaleDateString(); } catch { return s; } };

const PROJECT_BADGE = {
  New: "bg-[#EAF7FC] text-[#133955]", Acknowledged: "bg-[#EAF7FC] text-[#133955]", Contacted: "bg-[#EAF7FC] text-[#133955]",
  "Visit Scheduled": "bg-[#FFE8B0] text-[#7a5b00]", "Quote Sent": "bg-[#FFE8B0] text-[#7a5b00]",
  "Contract Signed": "bg-[#D8F3E3] text-[#0f6b3d]", "In Progress": "bg-[#D8F3E3] text-[#0f6b3d]", Completed: "bg-[#D8F3E3] text-[#0f6b3d]",
  "On Hold": "bg-[#FBD5D5] text-[#9b1c1c]", Lost: "bg-[#FBD5D5] text-[#9b1c1c]", Cancelled: "bg-[#FBD5D5] text-[#9b1c1c]",
};
const REPAIR_BADGE = {
  Submitted: "bg-[#EAF7FC] text-[#133955]", Assigned: "bg-[#EAF7FC] text-[#133955]",
  "Walkthrough Requested": "bg-[#FFE8B0] text-[#7a5b00]", "Walkthrough Scheduled": "bg-[#FFE8B0] text-[#7a5b00]",
  Quoted: "bg-[#FFE8B0] text-[#7a5b00]", "Quote Accepted": "bg-[#D8F3E3] text-[#0f6b3d]",
  "Quote Declined": "bg-[#FBD5D5] text-[#9b1c1c]", Completed: "bg-[#D8F3E3] text-[#0f6b3d]", Cancelled: "bg-[#FBD5D5] text-[#9b1c1c]",
};

export default function AdminContractorProfile() {
  const { cid } = useParams();
  const { token, role, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const saveEdit = async (form) => {
    const payload = { ...form };
    if (payload.referral_percentage === "" || payload.referral_percentage == null) delete payload.referral_percentage;
    else payload.referral_percentage = Number(payload.referral_percentage);
    await axios.patch(`${API}/admin/contractors/${cid}`, payload, authHeader);
    toast.success("Contractor profile updated.");
    setEditOpen(false);
    await load();
  };

  useEffect(() => {
    if (!authLoading && !token) navigate("/admin/login");
    if (!authLoading && token && role && role !== "admin") navigate(role === "agent" ? "/agent" : "/portal");
  }, [authLoading, token, role, navigate]);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/contractors/${cid}/profile`, { headers: { Authorization: `Bearer ${token}` } });
    setData(data);
  }, [cid, token]);

  useEffect(() => {
    if (!token) return;
    (async () => { try { await load(); } catch (e) { if (e?.response?.status === 401) { logout(); navigate("/admin/login"); } } finally { setLoading(false); } })();
  }, [token, load, logout, navigate]);

  if (authLoading || loading) return <div className="min-h-screen grid place-items-center bg-[#EAF7FC]"><Loader2 className="animate-spin text-[#133955]" size={32} /></div>;
  if (!data) return (
    <div className="min-h-screen grid place-items-center bg-[#EAF7FC]" data-testid="contractor-profile-missing">
      <div className="text-center"><p className="text-[#173042] font-semibold">Contractor not found.</p>
        <button onClick={() => navigate("/admin/contractors")} className="mt-4 rounded-full bg-[#133955] text-white px-6 py-2.5 text-sm font-bold">Back to Contractors</button></div>
    </div>
  );

  const c = data.contractor;
  const t = data.totals || {};
  const name = c.dba_name || c.legal_name;

  return (
    <div className="min-h-screen bg-[#EAF7FC]" data-testid="admin-contractor-profile">
      <header className="bg-white border-b border-[rgba(19,57,85,0.08)] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <button data-testid="nav-back-contractors" onClick={() => navigate("/admin/contractors")} className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold text-[#133955] hover:bg-[#EAF7FC]"><ArrowLeft size={16} /> Contractors</button>
            <NotificationBell />
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#133955] text-[#133955] px-5 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white"><LogOut size={16} /> Sign Out</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        {/* Profile card */}
        <div className="rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] p-6 mb-6" data-testid="contractor-profile-card">
          <div className="flex items-start gap-4 flex-wrap">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-[#133955] text-white shrink-0"><Building2 size={26} /></span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-bold text-[#54BCE6]">{c.contractor_ref}</span>
                <h1 className="font-display text-3xl font-extrabold text-[#133955] tracking-tight">{name}</h1>
                {c.is_real_estate && <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#FFE8B0] text-[#7a5b00]" data-testid="profile-re-badge"><Star size={12} /> Real Estate</span>}
                <span className="rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#D8F3E3] text-[#0f6b3d] capitalize" data-testid="profile-approval-badge">{c.approval_status}</span>
                <span className="rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#EAF7FC] text-[#133955] capitalize" data-testid="profile-account-badge">Account: {c.account_status}</span>
                <button data-testid="edit-contractor-btn" onClick={() => setEditOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#133955] text-[#133955] px-4 py-1.5 text-xs font-bold hover:bg-[#133955] hover:text-white transition-colors">
                  <Pencil size={13} /> Edit Profile
                </button>
              </div>
              {c.dba_name && c.legal_name && c.legal_name !== c.dba_name && <p className="text-sm text-[#173042]/60 mt-0.5">Legal: {c.legal_name}</p>}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1.5 mt-4 text-sm">
                <div><span className="text-[#173042]/50">Contact:</span> <strong className="text-[#173042]">{c.contact_name || "—"}</strong></div>
                <div className="break-all"><span className="text-[#173042]/50">Email:</span> {c.email}</div>
                <div><span className="text-[#173042]/50">Phone:</span> {c.phone || "—"}</div>
                <div><span className="text-[#173042]/50">Agreed Referral:</span> <strong className="text-[#133955]">{c.referral_percentage != null ? `${c.referral_percentage}%` : "Not set"}</strong></div>
                <div className="sm:col-span-2"><span className="text-[#173042]/50">Services:</span> {(c.services || []).join(", ") || "—"}</div>
                <div className="sm:col-span-2"><span className="text-[#173042]/50">Service Areas:</span> {(c.service_areas || []).join(", ") || "—"}</div>
                {c.license_number && <div><span className="text-[#173042]/50">License #:</span> {c.license_number}</div>}
                {c.insurance_carrier && <div className="sm:col-span-2"><span className="text-[#173042]/50">Insurance:</span> {c.insurance_carrier}{c.insurance_policy ? ` · ${c.insurance_policy}` : ""}{c.insurance_expiry ? ` · exp ${c.insurance_expiry}` : ""}</div>}
              </div>
            </div>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="contractor-totals">
          {[
            { icon: Home, label: "Projects Assigned", value: data.projects.length, testid: "total-projects" },
            { icon: Wrench, label: "Repair Requests", value: data.repairs.length, testid: "total-repairs" },
            { icon: FileText, label: `Contracts (${t.contracts_count || 0})`, value: money(t.contract_value_total), testid: "total-contract-value" },
            { icon: DollarSign, label: "Referral Fees Earned", value: money(t.referral_fee_total), testid: "total-referral-fees" },
          ].map((s) => (
            <div key={s.testid} className="rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] p-4" data-testid={s.testid}>
              <div className="flex items-center gap-2 text-[#173042]/60 text-xs font-bold uppercase tracking-wide"><s.icon size={14} className="text-[#54BCE6]" />{s.label}</div>
              <p className="font-display text-2xl font-extrabold text-[#133955] mt-1">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Projects */}
        <section className="mb-8">
          <h2 className="font-display text-xl font-extrabold text-[#133955] mb-3">Projects <span className="text-sm font-semibold text-[#173042]/50">({data.projects.length})</span></h2>
          {data.projects.length === 0 ? (
            <p className="rounded-xl bg-white border border-[rgba(19,57,85,0.08)] p-5 text-sm text-[#173042]/60" data-testid="no-profile-projects">No projects assigned yet.</p>
          ) : (
            <div className="space-y-2" data-testid="profile-projects">
              {data.projects.map((p) => (
                <button key={p.id} data-testid={`profile-project-${p.id}`} onClick={() => navigate(`/admin?lead=${p.id}`)}
                  className="w-full text-left rounded-xl bg-white border border-[rgba(19,57,85,0.08)] px-4 py-3 flex items-center gap-3 flex-wrap hover:border-[#54BCE6] transition-colors">
                  <span className="font-mono text-xs font-bold text-[#54BCE6]">#{String(p.lead_number || 0).padStart(4, "0")}</span>
                  <span className="font-semibold text-[#133955]">{p.name}</span>
                  <span className="text-sm text-[#173042]/60">{p.project_type} · {p.city}</span>
                  <span className="ml-auto flex items-center gap-2">
                    <span className="text-xs text-[#173042]/50">assigned {fmt(p.assigned_at)}</span>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${PROJECT_BADGE[p.project_status] || PROJECT_BADGE.New}`} data-testid={`profile-project-status-${p.id}`}>{p.project_status || "New"}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Repair requests */}
        <section>
          <h2 className="font-display text-xl font-extrabold text-[#133955] mb-3">Repair Requests <span className="text-sm font-semibold text-[#173042]/50">({data.repairs.length})</span></h2>
          {data.repairs.length === 0 ? (
            <p className="rounded-xl bg-white border border-[rgba(19,57,85,0.08)] p-5 text-sm text-[#173042]/60" data-testid="no-profile-repairs">No repair requests assigned yet.</p>
          ) : (
            <div className="space-y-2" data-testid="profile-repairs">
              {data.repairs.map((r) => (
                <button key={r.id} data-testid={`profile-repair-${r.id}`} onClick={() => navigate(`/admin/repair-requests?repair=${r.id}`)}
                  className="w-full text-left rounded-xl bg-white border border-[rgba(19,57,85,0.08)] px-4 py-3 flex items-center gap-3 flex-wrap hover:border-[#54BCE6] transition-colors">
                  <span className="font-mono text-xs font-bold text-[#54BCE6]">{r.request_ref}</span>
                  <span className="font-semibold text-[#133955]">{r.property_address}</span>
                  <span className="text-sm text-[#173042]/60">Agent: {r.agent_name}</span>
                  <span className="ml-auto flex items-center gap-2">
                    <span className="text-xs text-[#173042]/50">assigned {fmt(r.assigned_at)}</span>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${REPAIR_BADGE[r.status] || REPAIR_BADGE.Submitted}`} data-testid={`profile-repair-status-${r.id}`}>{r.status}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      </main>

      <EditContractorDialog open={editOpen} onClose={() => setEditOpen(false)} contractor={c} onSave={saveEdit} />
    </div>
  );
}

// Admin: edit contractor profile details (email changes sync their login account)
function EditContractorDialog({ open, onClose, contractor, onSave }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && contractor) {
      setForm({
        legal_name: contractor.legal_name || "", dba_name: contractor.dba_name || "",
        contact_name: contractor.contact_name || "", email: contractor.email || "",
        phone: contractor.phone || "", address: contractor.address || "",
        services: (contractor.services || []).join(", "), service_areas: (contractor.service_areas || []).join(", "),
        license_number: contractor.license_number || "", insurance_carrier: contractor.insurance_carrier || "",
        insurance_policy: contractor.insurance_policy || "", insurance_expiry: contractor.insurance_expiry || "",
        referral_percentage: contractor.referral_percentage != null ? String(contractor.referral_percentage) : "",
        is_real_estate: !!contractor.is_real_estate,
      });
    }
  }, [open, contractor]);

  if (!open || !form) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.legal_name.trim() || !form.email.trim()) { toast.error("Legal name and email are required."); return; }
    setBusy(true);
    try {
      await onSave({
        ...form,
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
        service_areas: form.service_areas.split(",").map((s) => s.trim()).filter(Boolean),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save changes.");
    } finally { setBusy(false); }
  };

  const fld = (key, label, opts = {}) => (
    <div className={opts.full ? "sm:col-span-2" : ""}>
      <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">{label}</label>
      <input data-testid={`edit-c-${key}`} type={opts.type || "text"} value={form[key]} onChange={set(key)} className="hpc-input bg-white" placeholder={opts.placeholder || ""} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="edit-contractor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#133955] text-2xl">Edit Contractor</DialogTitle>
          <DialogDescription className="text-[#173042]/70">
            Changes save immediately. Editing the email changes their sign-in email too.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 py-1">
          {fld("legal_name", "Legal Name *")}
          {fld("dba_name", "DBA / Display Name")}
          {fld("contact_name", "Contact Person")}
          {fld("phone", "Phone")}
          {fld("email", "Email (sign-in) *", { full: true, type: "email" })}
          {fld("address", "Address", { full: true })}
          {fld("services", "Services (comma-separated)", { full: true, placeholder: "Roofing, Siding" })}
          {fld("service_areas", "Service Areas (comma-separated)", { full: true, placeholder: "Charleston, Summerville" })}
          {fld("license_number", "License #")}
          {fld("referral_percentage", "Agreed Referral %", { type: "number" })}
          {fld("insurance_carrier", "Insurance Carrier")}
          {fld("insurance_policy", "Policy #")}
          {fld("insurance_expiry", "Insurance Expiry", { placeholder: "2027-01-31" })}
          <label className="flex items-center gap-2.5 sm:col-span-2 cursor-pointer">
            <input type="checkbox" data-testid="edit-c-is_real_estate" checked={form.is_real_estate} onChange={(e) => setForm((f) => ({ ...f, is_real_estate: e.target.checked }))} className="w-4 h-4 accent-[#133955]" />
            <span className="text-sm font-semibold text-[#173042]">Real Estate Contractor (suggested for repair-quote requests)</span>
          </label>
        </div>
        <DialogFooter className="gap-3">
          <button data-testid="edit-c-cancel" onClick={onClose} className="rounded-full border-2 border-[#133955] text-[#133955] px-6 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white transition-colors">Cancel</button>
          <button data-testid="edit-c-save" onClick={submit} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-6 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-50">
            {busy && <Loader2 size={16} className="animate-spin" />} {busy ? "Saving…" : "Save Changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
